import React, { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [siteSettings, setSiteSettings] = useState({
    phone: "781-870-7976",
    email: "contact@brendollysautoparts.com",
    address: "72 Chelsea Street Everett MA 02149",
    support_hours: "Mon-Fri: 8AM-6PM, Sat: 9AM-4PM, Sun: Closed",
    whatsapp: ""
  });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  // Fetch site settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/site-info`);
        if (response.data) {
          setSiteSettings(response.data);
        }
      } catch (error) {
        console.error("Error fetching site settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone",
      content: siteSettings.phone,
      link: `tel:${siteSettings.phone.replace(/[^0-9+]/g, '')}`
    },
    {
      icon: Mail,
      title: "Email",
      content: siteSettings.email,
      link: `mailto:${siteSettings.email}`
    },
    {
      icon: MapPin,
      title: "Address",
      content: siteSettings.address,
      link: null
    },
    {
      icon: Clock,
      title: "Support Hours",
      content: siteSettings.support_hours,
      link: null
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background animate-fade-in" data-testid="contact-page">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 py-16">
        <div className="container mx-auto px-4">
          <span className="text-primary font-medium uppercase tracking-wider">Get In Touch</span>
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white uppercase tracking-tight mt-2" data-testid="page-title">
            Contact Us
          </h1>
          <p className="text-zinc-400 mt-4 max-w-xl text-lg">
            Have a question about a product? Need help finding the right part? Our team is here to help.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="space-y-6">
            {contactInfo.map((item, index) => (
              <div 
                key={index}
                className="bg-zinc-900 border border-zinc-800 p-6 flex items-start gap-4"
                data-testid={`contact-info-${index}`}
              >
                <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-white font-heading text-lg uppercase">{item.title}</h3>
                  {item.link ? (
                    <a href={item.link} className="text-zinc-400 hover:text-primary transition-colors whitespace-pre-line">
                      {item.content}
                    </a>
                  ) : (
                    <p className="text-zinc-400 whitespace-pre-line">{item.content}</p>
                  )}
                </div>
              </div>
            ))}

            {/* Map placeholder */}
            <div className="bg-zinc-900 border border-zinc-800 aspect-video overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d188820.79560779825!2d-71.01625!3d42.3601!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e3652d0d3d311b%3A0x787cbf240162e8a0!2sBoston%2C%20MA!5e0!3m2!1sen!2sus!4v1615000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Store Location"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-zinc-900 border border-zinc-800 p-8">
              <h2 className="font-heading text-2xl font-bold text-white uppercase mb-6">Send Us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-zinc-400">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="mt-1 bg-zinc-950 border-zinc-700 text-white"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-zinc-400">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className="mt-1 bg-zinc-950 border-zinc-700 text-white"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="text-zinc-400">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(XXX) XXX-XXXX"
                      className="mt-1 bg-zinc-950 border-zinc-700 text-white"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject" className="text-zinc-400">Subject *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="How can we help?"
                      className="mt-1 bg-zinc-950 border-zinc-700 text-white"
                      required
                      data-testid="contact-subject"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-zinc-400">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about your inquiry..."
                    className="mt-1 bg-zinc-950 border-zinc-700 text-white min-h-[150px]"
                    required
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider px-8 py-3"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    "Sending..."
                  ) : (
                    <>
                      Send Message
                      <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
